import { Alert_2, MasterInfoEnum } from '@/constant';
import {
	useCommonList,
	useRowSelection,
	useSearchFilterRef,
	useToggle,
} from '@/hooks';
import { useMergingState } from '@/hooks';
import { IContractedForeignBusiness } from '@/services';
import { ContractedForeignBusinessActions } from '@/stores';
import { showAlert } from '@/utils';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import { getColumns, TYPE_SEARCH_UNIT_OPTIONS } from './config';
import { SelectForeignUnitModalViewProps, TFilter } from './type';

interface SearchForeignUnit {
	searchValue: string;
	searchType: string;
}

export const useLogic = (props: SelectForeignUnitModalViewProps) => {
	const { allowSelectMultiple = false } = props;

	const { t } = useTranslation('common');

	const rowSelection = useRowSelection<any, IContractedForeignBusiness>({
		hideSelectAll: !allowSelectMultiple,
		isSingle: !allowSelectMultiple,
	});

	const defaultFilter = {
		valueSelect: TYPE_SEARCH_UNIT_OPTIONS[0].value,
		keyword: '',
	};
	const [filter, setFilter] = useMergingState<TFilter>(defaultFilter);

	const resetFilter = useCallback(() => {
		setFilter(defaultFilter);
		setParamSearch({
			searchValue: '',
			searchType: defaultFilter.valueSelect,
		});
		rowSelection.onReset();
	}, []);

	const [openModal, onToggle] = useToggle(false, resetFilter);

	const [paramSearch, setParamSearch] = useState<SearchForeignUnit>({
		searchValue: '',
		searchType: '',
	});

	const [isSearching, setIsSearching] = useState<boolean>(false);
	const [isSaving, setIsSaving] = useState<boolean>(false);

	const {
		commonList: { fetching, data, total },
		fetchData,
	} = useCommonList<IContractedForeignBusiness>(
		MasterInfoEnum.FOREIGN_BUSINESS_UNIT,
		openModal,
		{ ...paramSearch }
	);

	const dispatch = useDispatch();

	const searchFilterRef = useSearchFilterRef({
		onSearchChange: fetchData,
	});

	useEffect(() => {
		if (searchFilterRef.current) {
			searchFilterRef.current.onSearchChange = fetchData;
		}
	}, [fetchData]);

	const handleSave = useCallback(() => {
		if (rowSelection?.selectedRowKeys?.length === 0) {
			showAlert(Alert_2('해외 사업체'));
			return;
		}
		setIsSaving(true);
		const selectedRowKeys = rowSelection?.selectedRowKeys || [];

		dispatch(
			ContractedForeignBusinessActions.addContractedForeignBusinessRequest({
				ids: selectedRowKeys.map(Number),
				callback: (error) => {
					if (error) {
						setIsSaving(false);
						return;
					}
					props?.fetchData?.();
					rowSelection.onReset();
					onToggle();
					setIsSaving(false);
					return;
				},
			})
		);
	}, [onToggle, rowSelection, props?.fetchData]);

	const handleChangeSelect = useCallback(
		(value: string) => {
			setFilter({
				keyword: '',
				valueSelect: value,
			});
		},
		[setFilter]
	);

	const handleChangeKeyword = useCallback(
		(e: React.ChangeEvent<HTMLInputElement>) => {
			setFilter({ keyword: e.target.value });
		},
		[setFilter]
	);

	const handleSelectNational = useCallback(
		(value: string) => {
			setFilter({ keyword: value });
		},
		[setFilter]
	);

	const onSearch = useCallback(() => {
		setParamSearch({
			searchValue: filter?.keyword,
			searchType: filter?.valueSelect,
		});
		setIsSearching(true);
	}, [setFilter]);

	const onPageChange = useCallback((page: number) => {
		setParamSearch((prev) => ({ ...prev, page }));
	}, []);

	return {
		openModal,
		dataSource: data,
		columns: getColumns(t),
		rowSelection,
		searchFilterRef,
		onToggle,
		handleSave,
		loading: fetching,
		filter,
		handleChangeSelect,
		handleChangeKeyword,
		handleSelectNational,
		onSearch,
		isSearching,
		total,
		onPageChange,
		isSaving,
	};
};
